// This is a temporary implementation of Revieve VTO for evaluating their out-of-the-box solution

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorNeutralDarkGrey } from 'autogen/design-tokens/tokens';
import type { VariantPickerListItem } from 'autogen/swagger/all';
import { GoToProduct, LoadingError } from 'autogen/translation-keys/trans-website-product-vto';
import {
    AcceptCookiesToSeeElementHeadline,
    AcceptFunctionalCookiesToSeeElementBody
} from 'autogen/translation-keys/trans-website-shared';
import { Button } from 'components/Button';
import CookieInfobox from 'components/CookieInfobox';
import ColoredInfobox from 'components/generic/ColoredInfobox';
import QuickView from 'components/quickView';
import ActionFooter from 'components/quickView/components/ActionFooter';
import AbsoluteQuickViewHeader from 'components/quickView/components/QuickViewWithAbsoluteHeader/Header';
import { useSessionState } from 'hooks/storage';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import { breakpoint, sizes } from 'variables';
import { REVIEVE_PRODUCTS_TRACKING_SESSION_KEY } from '.';
import VariantSelector from './components/VariantSelector';
import useRevieveVTOQuickView from './hooks/useRevieveVTOQuickView';

interface IRevieveConfig {
    partner_id: string;
    locale: string;
    disablePersist: boolean;
    env: string;
    renderTo?: HTMLElement | null;
}

const StyledQuickView = styled(QuickView.Controlled)`
    width: 100%;
    height: 90% !important;

    ${breakpoint.up(sizes.sm)} {
        width: 100% !important;
        max-width: 700px !important;
    }

    ${breakpoint.up(sizes.md)} {
        height: 80% !important;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const RevieveContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: ${ColorNeutralDarkGrey};
`;

const RevieveVTO = styled.div`
    width: 100%;
    height: 100%;
`;

const StyledColoredInfobox = styled(ColoredInfobox)`
    position: absolute;
    top: 50%;
    left: 5%;
    width: 90%;
`;

const StyledButton = styled(Button)`
    ${breakpoint.up(sizes.sm)} {
        align-self: end;
    }
`;

function RevieveVTOQuickView() {
    const [{ isOpen, variants, vtoConfig }, toggle] = useRevieveVTOQuickView();
    const selectedVariant = variants?.sections[0].variants.find((variant) => variant.isSelected);
    const [selectedItem, setSelectedItem] = useState<VariantPickerListItem | undefined>(selectedVariant);
    const [focusedItem, setFocusedItem] = useState<VariantPickerListItem | undefined>(selectedVariant);
    const [error, setError] = useState<string | undefined>();
    const [revieveProducts, setRevieveProducts] = useSessionState<number[]>(REVIEVE_PRODUCTS_TRACKING_SESSION_KEY, []);
    const revieveRef = useRef<HTMLDivElement>(null);
    const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);
    const { t } = useTranslation();

    let revieveConfig: IRevieveConfig;

    if (vtoConfig?.config) {
        revieveConfig = {
            partner_id: vtoConfig?.config?.partnerId,
            locale: vtoConfig?.config?.locale,
            disablePersist: true,
            env: vtoConfig?.config?.env
        };
    }

    const openPlugin = () => {
        const productId = vtoConfig?.config?.productId.toString();

        window.Revieve.API.liveAR.isTryOnAvailable({ product_id: productId }, (available) => {
            if (available) {
                window.Revieve.API.liveAR.addTryOnProduct(productId);
            }
        });

        window.Revieve.API.show();
    };

    const loadRevieveScript = () => {
        return new Promise((resolve, reject) => {
            const existingScript = document.getElementById('revieve_script');

            if (!existingScript) {
                const script = document.createElement('script');
                script.id = 'revieve_script';
                script.src = 'https://d38knilzwtuys1.cloudfront.net/revieve-plugin-v4/revieve-plugin-loader.js';
                script.async = true;
                script.onload = () => resolve(script);
                script.onerror = () => reject(new Error('Failed to load Revieve script'));
                const firstScript = document.getElementsByTagName('script')[0];
                firstScript.parentNode?.insertBefore(script, firstScript);
            }
        });
    };

    const initRevieveConfig = () => {
        return new Promise<void>((resolve, reject) => {
            if (!window.Revieve._config()) {
                revieveConfig.renderTo = revieveRef.current;

                window.Revieve.Init(revieveConfig, () => {
                    resolve();
                });
            } else {
                reject();
            }
        });
    };

    const initVTO = async () => {
        if (!window.Revieve) {
            try {
                await loadRevieveScript();
                await initRevieveConfig();
            } catch (error) {
                console.error('Error loading or initializing the plugin', error);
            }
        }

        openPlugin();
    };

    let interval;

    function checkForRevieveRef() {
        if (revieveRef.current) {
            initVTO();
            clearInterval(interval);
        }
    }

    useEffect(() => {
        if (isFunctionalCookieAccepted) {
            // setting interval to check for revieve ref
            interval = setInterval(() => {
                checkForRevieveRef();
            }, 1000);

            // used to clear interval if no ref is found within 10 seconds
            setTimeout(() => {
                clearInterval(interval);

                if (!revieveRef.current) {
                    setError(t(LoadingError));
                }
            }, 10000);

            return () => {
                clearInterval(interval);

                if (window.Revieve) {
                    window.Revieve.API.liveAR.resetTryOnProducts();
                }
            };
        }
    }, [isFunctionalCookieAccepted]);

    return (
        <StyledQuickView
            active={isOpen}
            onClose={() => {
                toggle((prevModel) => ({ ...prevModel, isOpen: false }));
            }}
            headerRenderer={AbsoluteQuickViewHeader}
            footerRenderer={() =>
                isFunctionalCookieAccepted && (
                    <ActionFooter>
                        <StyledButton
                            onClick={() => {
                                toggle({ isOpen: false });
                                if (selectedItem) {
                                    if (!revieveProducts.includes(selectedItem.productId)) {
                                        setRevieveProducts([...revieveProducts, selectedItem.productId]);
                                    }
                                    if (selectedItem.productId !== vtoConfig?.config?.productId) {
                                        // Only redirect if the selected variant is different from the current one.
                                        window.location.href = selectedItem.linkAction.url;
                                    }
                                }
                            }}
                            size="lg"
                        >
                            {t(GoToProduct)}
                        </StyledButton>
                    </ActionFooter>
                )
            }
        >
            {isFunctionalCookieAccepted ? (
                <Content>
                    <RevieveContainer>
                        <RevieveVTO ref={revieveRef} />
                        {error && <StyledColoredInfobox text={error} variant="Negative" />}
                    </RevieveContainer>

                    <VariantSelector
                        variants={variants?.sections[0].variants}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        focusedItem={focusedItem}
                        setFocusedItem={setFocusedItem}
                        setError={setError}
                    />
                </Content>
            ) : (
                <RevieveContainer>
                    <CookieInfobox
                        headline={t(AcceptCookiesToSeeElementHeadline)}
                        body={t(AcceptFunctionalCookiesToSeeElementBody)}
                    />
                </RevieveContainer>
            )}
        </StyledQuickView>
    );
}

export default RevieveVTOQuickView;
